import React, { useState, useEffect, useCallback } from 'react';
import {
    HiOutlineSearch, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash,
    HiOutlineUpload, HiOutlineDownload, HiOutlineRefresh
} from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';

const EMPTY = {
    employeeCode: '', fullName: '', mobile: '', email: '', company: '', department: '',
    branch: '', designation: '', qid: '', isActive: true
};

const EmployeesTab = ({ canCreate, canEdit, canDelete }) => {
    const [employees, setEmployees] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [editing, setEditing] = useState(null); // null | {..} (new when no _id)
    const [importResult, setImportResult] = useState(null);

    const fetchEmployees = useCallback(async (term) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/payslips/employees', {
                params: { search: term || '', limit: 200 }
            });
            setEmployees(res.data?.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load employees');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchEmployees(search), 300);
        return () => clearTimeout(t);
    }, [search, fetchEmployees]);

    const save = async () => {
        if (!editing) return;
        const payload = {
            employeeCode: editing.employeeCode?.trim(),
            fullName: editing.fullName?.trim(),
            email: editing.email?.trim() || null,
            company: editing.company?.trim() || null,
            department: editing.department?.trim() || null,
            branch: editing.branch?.trim() || null,
            designation: editing.designation?.trim() || null,
            qid: editing.qid?.trim() || null,
            isActive: editing.isActive
        };

        // The list only ever holds a masked number, so the edit form starts blank.
        // Omitting the key entirely leaves the stored number untouched; sending ""
        // would be rejected as an empty mobile.
        const mobile = editing.mobile?.trim();
        if (mobile) {
            payload.mobile = mobile;
        } else if (!editing._id) {
            setError('Mobile number is required for a new employee.');
            return;
        }

        try {
            if (editing._id) {
                await axiosInstance.put(`/api/payslips/employees/${editing._id}`, payload);
                setNotice('Employee updated');
            } else {
                await axiosInstance.post('/api/payslips/employees', payload);
                setNotice('Employee added');
            }
            setEditing(null);
            fetchEmployees(search);
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        }
    };

    const remove = async (employee) => {
        if (!window.confirm(`Remove ${employee.fullName} (${employee.employeeCode}) from the master table?`)) return;
        try {
            await axiosInstance.delete(`/api/payslips/employees/${employee._id}`);
            fetchEmployees(search);
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        }
    };

    const importSheet = async (file) => {
        if (!file) return;
        const form = new FormData();
        form.append('file', file);
        setImportResult(null);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/payslips/employees/bulk-upload', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportResult(res.data?.data || null);
            setNotice(res.data?.message || 'Import complete');
            fetchEmployees(search);
        } catch (err) {
            setError(err.response?.data?.message || 'Import failed');
        }
    };

    const downloadTemplate = async () => {
        try {
            const res = await axiosInstance.get('/api/payslips/employees/template', { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'employee-master-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setError('Could not download the template');
        }
    };

    return (
        <div className="pd-stack">
            <div className="pd-card">
                <div className="pd-card-head">
                    <div>
                        <h3>Employee master</h3>
                        <p className="pd-muted">
                            Payslip PDFs carry no phone number — this table is the only routing source. A code
                            missing here cannot be delivered.
                        </p>
                    </div>
                    <div className="pd-actions">
                        <button className="pd-btn pd-btn-ghost" onClick={downloadTemplate}>
                            <HiOutlineDownload size={16} /> Template
                        </button>
                        {canCreate && (
                            <label className="pd-btn pd-btn-ghost">
                                <HiOutlineUpload size={16} /> Import Excel
                                <input
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    hidden
                                    onChange={(e) => { importSheet(e.target.files?.[0]); e.target.value = ''; }}
                                />
                            </label>
                        )}
                        {canCreate && (
                            <button className="pd-btn pd-btn-primary" onClick={() => setEditing({ ...EMPTY })}>
                                <HiOutlinePlus size={16} /> Add employee
                            </button>
                        )}
                    </div>
                </div>

                <div className="pd-toolbar">
                    <div className="pd-search">
                        <HiOutlineSearch size={16} />
                        <input
                            placeholder="Search by code or name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button className="pd-btn pd-btn-ghost" onClick={() => fetchEmployees(search)} disabled={loading}>
                        <HiOutlineRefresh size={16} /> Refresh
                    </button>
                </div>

                {error && <div className="pd-banner pd-banner-err">{error}</div>}
                {notice && <div className="pd-banner pd-banner-ok">{notice}</div>}

                {importResult && (
                    <div className={`pd-banner ${importResult.failedCount ? 'pd-banner-warn' : 'pd-banner-ok'}`}>
                        <strong>
                            {importResult.created} added, {importResult.updated} updated
                            {importResult.failedCount ? `, ${importResult.failedCount} rejected` : ''}
                        </strong>
                        {importResult.failedCount > 0 && (
                            <ul className="pd-fail-list">
                                {importResult.failed.slice(0, 20).map((f, i) => (
                                    <li key={i}>Row {f.row}{f.employeeCode ? ` (${f.employeeCode})` : ''}: {f.reason}</li>
                                ))}
                                {importResult.failed.length > 20 && <li>…and {importResult.failed.length - 20} more</li>}
                            </ul>
                        )}
                    </div>
                )}

                {loading ? (
                    <p className="pd-muted">Loading…</p>
                ) : employees.length === 0 ? (
                    <p className="pd-muted">No employees yet. Import an Excel sheet to get started.</p>
                ) : (
                    <div className="pd-table-wrap">
                        <table className="pd-table">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Mobile</th>
                                    <th>Email</th>
                                    <th>Department</th>
                                    <th>Active</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((employee) => (
                                    <tr key={employee._id} className={employee.isActive ? '' : 'pd-row-muted'}>
                                        <td className="pd-mono">{employee.employeeCode}</td>
                                        <td>{employee.fullName}</td>
                                        <td className="pd-mono">{employee.mobile}</td>
                                        <td className="pd-truncate">{employee.email || '—'}</td>
                                        <td>{employee.department || '—'}</td>
                                        <td>
                                            <span className={`pd-pill ${employee.isActive ? 'pd-pill-ok' : 'pd-pill-muted'}`}>
                                                {employee.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="pd-row-actions">
                                            {canEdit && (
                                                <button className="pd-icon-btn" title="Edit" onClick={() => setEditing({ ...employee, mobile: '' })}>
                                                    <HiOutlinePencil size={16} />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button className="pd-icon-btn pd-icon-danger" title="Delete" onClick={() => remove(employee)}>
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing && (
                <div className="pd-modal-backdrop" onClick={() => setEditing(null)}>
                    <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{editing._id ? `Edit ${editing.employeeCode}` : 'Add employee'}</h3>

                        <div className="pd-field">
                            <label>Employee code</label>
                            <input
                                value={editing.employeeCode}
                                disabled={Boolean(editing._id)}
                                onChange={(e) => setEditing({ ...editing, employeeCode: e.target.value })}
                                placeholder="B1169"
                            />
                        </div>
                        <div className="pd-field">
                            <label>Full name</label>
                            <input
                                value={editing.fullName}
                                onChange={(e) => setEditing({ ...editing, fullName: e.target.value })}
                            />
                        </div>
                        <div className="pd-field">
                            <label>Mobile {editing._id && <span className="pd-muted">(leave blank to keep unchanged)</span>}</label>
                            <input
                                value={editing.mobile}
                                onChange={(e) => setEditing({ ...editing, mobile: e.target.value })}
                                placeholder="+974 5514 9207"
                            />
                            <small className="pd-muted">Local 8-digit numbers are assumed to be Qatar.</small>
                        </div>
                        <div className="pd-field">
                            <label>Email <span className="pd-muted">(optional)</span></label>
                            <input
                                value={editing.email || ''}
                                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                            />
                        </div>
                        <div className="pd-field-row">
                            <div className="pd-field">
                                <label>Company</label>
                                <input
                                    value={editing.company || ''}
                                    onChange={(e) => setEditing({ ...editing, company: e.target.value })}
                                />
                            </div>
                            <div className="pd-field">
                                <label>Department</label>
                                <input
                                    value={editing.department || ''}
                                    onChange={(e) => setEditing({ ...editing, department: e.target.value })}
                                />
                            </div>
                        </div>
                        {/* HR details — also used to pre-fill the clearance form. */}
                        <div className="pd-field-row">
                            <div className="pd-field">
                                <label>Branch</label>
                                <input
                                    value={editing.branch || ''}
                                    onChange={(e) => setEditing({ ...editing, branch: e.target.value })}
                                />
                            </div>
                            <div className="pd-field">
                                <label>Designation</label>
                                <input
                                    value={editing.designation || ''}
                                    onChange={(e) => setEditing({ ...editing, designation: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="pd-field">
                            <label>QID <span className="pd-muted">(optional)</span></label>
                            <input
                                value={editing.qid || ''}
                                onChange={(e) => setEditing({ ...editing, qid: e.target.value })}
                                placeholder="29182603456"
                            />
                        </div>
                        {editing._id && (
                            <label className="pd-check">
                                <input
                                    type="checkbox"
                                    checked={editing.isActive}
                                    onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                                />
                                Active — inactive employees are never sent payslips
                            </label>
                        )}

                        <div className="pd-actions pd-actions-end">
                            <button className="pd-btn pd-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                            <button className="pd-btn pd-btn-primary" onClick={save}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeesTab;
